import { Injectable } from '@angular/core';
import { APP_CONFIG } from '../../../config/app.config';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { DateService } from '../date/date.service';
import { ENV } from '../../../config/env.config';

// Import Firebase modules correctly (Firebase v9+)
import { initializeApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getDatabase, ref, set, get } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Initialize Firebase app
const firebaseApp = initializeApp(ENV.FIREBASE_CONFIG);

const db = getDatabase(firebaseApp);
const auth = getAuth(firebaseApp);
const functions = getFunctions(firebaseApp, 'europe-west1');

@Injectable({
  providedIn: 'root',
})
export class DatabaseService {
  user;
  refJimmyUserActivity = APP_CONFIG.REF_JIMI_USER_ACTIVITY;
  refJimmyUserConnection = APP_CONFIG.REF_JIMI_USER_CONNECTION;

  constructor(
    public http: HttpClient,
    private logger: NGXLogger,
    private dateService: DateService
  ) {
    this.logger.debug('Hello DatabaseService Service');
  }

  callCloudFunction(functionName, param) {
    this.logger.debug(
      `Call to callCloudFunction() with functionName = ${functionName} and param = ${JSON.stringify(param, undefined, 3)}`
    );
    const cf = httpsCallable(functions, functionName);
    return cf(param);
  }

  writeUserActivityData(userActivity: any): Promise<any> {
    this.logger.debug('writeUserActivityData >>> ' + JSON.stringify(userActivity));
    return set(ref(db, this.refJimmyUserActivity + this.getFirebaseUser().uid), userActivity);
  }

  writeUpdateAlerte(updateAlerteAction: any): Promise<any> {
    this.logger.debug('writeUpdateAlerte >>> ' + JSON.stringify(updateAlerteAction));
    return set(ref(db, `${this.refJimmyUserActivity}${this.getFirebaseUser().uid}/updateAlerte`), updateAlerteAction);
  }

  writeCreateAlerte(alerteDetail, email, context, emailCopieCachee, addressBefore?): Promise<any> {
    const request = this.buildCreateAlerteAction(alerteDetail, email, context, emailCopieCachee, addressBefore);
    this.logger.debug('writeCreateAlerte >>> ' + JSON.stringify(request));
    return set(ref(db, `${this.refJimmyUserActivity}${this.getFirebaseUser().uid}/createAlerte`), request);
  }

  writeUserConnection(firebaseUser) {
    return set(ref(db, this.refJimmyUserConnection + firebaseUser.uid), this.buildUserConnection(firebaseUser));
  }

  initUserActivity(registrationToken): Promise<any> {
    return get(ref(db, `${this.refJimmyUserActivity}${this.getFirebaseUser().uid}/registrationToken`))
      .then((results) => {
        if (results.val() !== registrationToken) {
          return this.writeUserActivityData(this.buildDefaultUserActivity(registrationToken));
        }
      });
  }

  clearregistrationToken(): Promise<any> {
    const firebaseUser = this.getFirebaseUser();
    if (firebaseUser) {
      return set(ref(db, `${this.refJimmyUserActivity}${firebaseUser.uid}/registrationToken`), '');
    }
    return Promise.resolve();
  }

  buildUserConnection(firebaseUser) {
    return {
      userConnection: {
        displayName: firebaseUser.displayName,
        email: firebaseUser.email,
        date: this.dateService.getFormatedDate(),
      },
    };
  }

  private buildCreateAlerteAction(alerteDetail, email, context, emailCopieCachee, addressBefore) {
    return {
      adresse: alerteDetail.adresse,
      alerteResponsableEmail: email,
      idAlerte: alerteDetail.id,
      typologie: alerteDetail.typeAlerte,
      idIntervention: alerteDetail.idIntervention ?? null,
      context,
      emailCopieCachee,
      addressBefore: addressBefore || '',
      status: alerteDetail.statut,
      dateCreation: alerteDetail.dateCreation,
    };
  }

  buildDefaultUserActivity(registrationToken) {
    return {
      createAlerte: {
        alerteResponsableEmail: '',
        idAlerte: 0,
        adresse: '',
        typologie: '',
      },
      updateAlerte: {
        alerteCreatorEmail: '',
        alerteId: '',
        interventionId: '',
        status: '',
      },
      email: this.getFirebaseUser()?.email || '',
      registrationToken: registrationToken || '',
    };
  }

  getFirebaseUser() {
    return auth.currentUser;
  }
}
